/**
 * Type definitions for commonly used model data structures
 *
 * @module types/model-common
 * @category Type
 *
 */
import { Dictionary, flatten, forEach, get, keys } from 'lodash';
import { ImpactCategoryId } from './input/impacts/impact-categories';

/**
 * Possible value chains relations for impacts
 */
export enum ValueChain {
  DOWNSTREAM = 'D',
  UPSTREAM = 'U',
  INTERNAL = 'I',
  ANY = 'A', // may be any value chain
}

export type ByValueChain<T> = { [V in ValueChain]: T };

/* Assert all keys are value chains, fill with default value and type accordingly. Throws error for non-valueChain keys */
export function toByValueChain<T>(valuesByVc: Dictionary<T>, defaultValue: T): ByValueChain<T> {
  forEach(keys(valuesByVc), key => {
    if (!(allValueChains as string[]).includes(key)) {
      throw Error(`Assertion error: the key ${key} is not a value chain`);
    }
  });
  return {
    [ValueChain.DOWNSTREAM]: get(valuesByVc, ValueChain.DOWNSTREAM, defaultValue),
    [ValueChain.UPSTREAM]: get(valuesByVc, ValueChain.UPSTREAM, defaultValue),
    [ValueChain.INTERNAL]: get(valuesByVc, ValueChain.INTERNAL, defaultValue),
    [ValueChain.ANY]: get(valuesByVc, ValueChain.ANY, defaultValue),
  };
}

export const allValueChains: ValueChain[] = [
  ValueChain.DOWNSTREAM,
  ValueChain.UPSTREAM,
  ValueChain.INTERNAL,
  ValueChain.ANY,
];

export function isValueChain(d?: string): d is ValueChain {
  return !!d && allValueChains.indexOf(d as ValueChain) > -1;
}

/**
 * Main impact dimensions
 */
export enum Dimension {
  SOCIAL = 'S',
  KNOWLEDGE = 'K',
  HEALTH = 'H',
  ENVIRONMENT = 'E',
}

export function isDimension(input: string | undefined): input is Dimension {
  return ORDERED_DIMENSIONS.includes(input as Dimension);
}

export const ORDERED_DIMENSIONS: Dimension[] = [
  Dimension.SOCIAL,
  Dimension.KNOWLEDGE,
  Dimension.HEALTH,
  Dimension.ENVIRONMENT,
];

export type ByDimension<T> = {
  [dimension in Dimension]: T;
};

export const ORDERED_IMPACTS_BY_DIMENSION: ByDimension<ImpactCategoryId[]> = {
  [Dimension.SOCIAL]: [
    ImpactCategoryId.JOBS,
    ImpactCategoryId.TAXES,
    ImpactCategoryId.SOCIETAL_INFRA,
    ImpactCategoryId.SOCIETAL_STABILITY,
    ImpactCategoryId.EQUALITY,
  ],
  [Dimension.KNOWLEDGE]: [
    ImpactCategoryId.KNOWLEDGE_INFRA,
    ImpactCategoryId.CREATES_KNOWLEDGE,
    ImpactCategoryId.DISTRIBUTING_KNOWLEDGE,
    ImpactCategoryId.SCARCE_HUMAN_CAPITAL,
  ],
  [Dimension.HEALTH]: [
    ImpactCategoryId.PHYSICAL_DISEASES,
    ImpactCategoryId.MENTAL_DISEASES,
    ImpactCategoryId.NUTRITION,
    ImpactCategoryId.RELATIONSHIPS,
    ImpactCategoryId.MEANING_AND_JOY,
  ],
  [Dimension.ENVIRONMENT]: [
    ImpactCategoryId.GHG_EMISSIONS,
    ImpactCategoryId.NON_GHG_EMISSIONS,
    ImpactCategoryId.SCARCE_NATURAL_RESOURCES,
    ImpactCategoryId.BIODIVERSITY,
    ImpactCategoryId.WASTE,
  ],
};

export const NUMBER_ORDERED_IMPACTS_BY_DIMENSION: {
  [dimension in Dimension]: ImpactCategoryId[];
} = {
  [Dimension.SOCIAL]: [
    ImpactCategoryId.TAXES,
    ImpactCategoryId.JOBS,
    ImpactCategoryId.SOCIETAL_INFRA,
    ImpactCategoryId.EQUALITY,
    ImpactCategoryId.SOCIETAL_STABILITY,
  ],
  [Dimension.KNOWLEDGE]: [
    ImpactCategoryId.SCARCE_HUMAN_CAPITAL,
    ImpactCategoryId.KNOWLEDGE_INFRA,
    ImpactCategoryId.CREATES_KNOWLEDGE,
    ImpactCategoryId.DISTRIBUTING_KNOWLEDGE,
  ],
  [Dimension.HEALTH]: [
    ImpactCategoryId.PHYSICAL_DISEASES,
    ImpactCategoryId.MENTAL_DISEASES,
    ImpactCategoryId.NUTRITION,
    ImpactCategoryId.RELATIONSHIPS,
    ImpactCategoryId.MEANING_AND_JOY,
  ],
  [Dimension.ENVIRONMENT]: [
    ImpactCategoryId.GHG_EMISSIONS,
    ImpactCategoryId.NON_GHG_EMISSIONS,
    ImpactCategoryId.SCARCE_NATURAL_RESOURCES,
    ImpactCategoryId.BIODIVERSITY,
    ImpactCategoryId.WASTE,
  ],
};

export const allDimensions: Dimension[] = ORDERED_DIMENSIONS;

/**
 * The intrinsic
 *  attractiviness/goodness (positive valence) or
 *  averseness/badness (negative valence) of an impact
 *
 * See https://en.wikipedia.org/wiki/Valence_(psychology)
 */
export enum Valence {
  POSITIVE = 'P',
  NEGATIVE = 'N',
  OTHER = 'O',
}

export type BySomeValences<T> = {
  [valence in Valence]?: T;
};

export function isValence(d?: string | Valence): d is Valence {
  return !!d && allValences.indexOf(d as Valence) > -1;
}

export function oppositeValence(valence: Valence.NEGATIVE | Valence.POSITIVE) {
  return valence === Valence.POSITIVE ? Valence.NEGATIVE : Valence.POSITIVE;
}

export const allValences: Valence[] = [Valence.POSITIVE, Valence.NEGATIVE, Valence.OTHER];

export function getValencesForCategory(impactCode: ImpactCategoryId | undefined) {
  switch (impactCode) {
    case ImpactCategoryId.TAXES:
    case ImpactCategoryId.JOBS:
    case ImpactCategoryId.SOCIETAL_INFRA:
      return [Valence.POSITIVE];
    case ImpactCategoryId.SCARCE_HUMAN_CAPITAL:
      return [Valence.NEGATIVE];
    default:
      return [Valence.POSITIVE, Valence.NEGATIVE];
  }
}

export enum ExtendedValence {
  POSITIVE = 'P',
  NEGATIVE = 'N',
  SMALL_NEGATIVE = 'S',
  OTHER = 'O',
}

export const allExtendedValences = Object.values(ExtendedValence).filter(val => typeof val === 'string');

export enum ImpactDirection {
  INCREASE = 'I',
  DECREASE = 'D',
}

export enum BibliometricSource {
  CORE = 1,
  WIKI = 2,
  CORE2 = 3,
}

export const orderedDimensions = ORDERED_DIMENSIONS;
export const orderedImpactsByDimension = ORDERED_IMPACTS_BY_DIMENSION;

export const orderedImpacts: ImpactCategoryId[] = flatten(orderedDimensions.map(d => orderedImpactsByDimension[d]));
export const numberOrderedImpacts: ImpactCategoryId[] = flatten(
  orderedDimensions.map(d => NUMBER_ORDERED_IMPACTS_BY_DIMENSION[d]),
);
export const orderedValueChains = [ValueChain.DOWNSTREAM, ValueChain.UPSTREAM, ValueChain.INTERNAL, ValueChain.ANY];
export const orderedValences = [Valence.POSITIVE, Valence.NEGATIVE];
export const ByValueChain = undefined;

export interface BibliometricUpdateBlock {
  productCode: string;
  source: BibliometricSource;
}

export interface GenericBibliometricUpdateBlock {
  productCode: string | undefined;
  source: BibliometricSource;
}
